/**
 * Price lists API client (BFF: `/api/v1/catalog/pricing/price-lists`) — adr-pricing-025,
 * plan-platform-002 WP2.4/WP4.2. Confirmed live against `web-merchant-portal-bff`'s
 * `openapi.yml` (2026-09-03): list CRUD, per-list bindings CRUD, and `storeCode` +
 * `priceListCodeUsed` on `price-resolution` (see `catalogPricingApi.ts`).
 *
 * **Contract gap (disclosed, coded against the frozen target shape — do not remove without
 * re-checking the live BFF):** `GET .../price-lines` still only accepts `productId` (required) —
 * there is no `priceListCode` (+ `page`/`size`) filter yet, even though
 * `docs/design/price-lists-workspace-spec.md` §3 marks that row "extend — WP2.4" and the
 * list-detail lines grid (§8.3) needs exactly that ("which products does THIS list price"). This
 * module still exposes {@link listPriceListLines}, calling the endpoint with the intended filter,
 * so the feature lights up the moment the BFF ships it; until then it will 400 (the endpoint's
 * `productId` param is `required: true` server-side) — the list-detail page catches that and
 * shows its own `ErrorState`, never a blank crash. The GRID's list-mode (`PriceGridPanel`) does
 * **not** depend on this gap at all — it resolves a list-scoped cell by filtering the SAME
 * per-product `GET .../price-lines?productId=` lines array (`useProductPriceLines`, already
 * fetched for base/channel modes) by `line.priceListCode`, since that field is additive on the
 * existing per-product endpoint.
 */
import { apiClient } from '@/shared/api';

import type {
  PriceListBindingResponse,
  PriceListRequest,
  PriceListResponse,
  RetailStoreOption,
} from '../model/priceListTypes';
import type { PriceLineResponse } from '../model/types';

const BASE = '/api/v1/catalog/pricing/price-lists';

export const listPriceLists = async (): Promise<PriceListResponse[]> =>
  (await apiClient.get<PriceListResponse[]>(BASE)).data;

export const getPriceList = async (code: string): Promise<PriceListResponse> =>
  (await apiClient.get<PriceListResponse>(`${BASE}/${encodeURIComponent(code)}`)).data;

export const createPriceList = async (body: PriceListRequest): Promise<PriceListResponse> =>
  (await apiClient.post<PriceListResponse>(BASE, body)).data;

/** Name/kind/active — `code` is immutable and ignored by the backend on update. */
export const updatePriceList = async (
  code: string,
  body: PriceListRequest,
): Promise<PriceListResponse> =>
  (await apiClient.put<PriceListResponse>(`${BASE}/${encodeURIComponent(code)}`, body)).data;

export const listPriceListBindings = async (code: string): Promise<PriceListBindingResponse[]> =>
  (
    await apiClient.get<PriceListBindingResponse[]>(
      `${BASE}/${encodeURIComponent(code)}/bindings`,
    )
  ).data;

/** Idempotent — re-binding a store already bound elsewhere MOVES the binding (never duplicates). */
export const bindPriceListStore = async (
  code: string,
  storeCode: string,
): Promise<PriceListBindingResponse> =>
  (
    await apiClient.put<PriceListBindingResponse>(
      `${BASE}/${encodeURIComponent(code)}/bindings/${encodeURIComponent(storeCode)}`,
    )
  ).data;

export const unbindPriceListStore = async (code: string, storeCode: string): Promise<void> => {
  await apiClient.delete(`${BASE}/${encodeURIComponent(code)}/bindings/${encodeURIComponent(storeCode)}`);
};

export interface ListPriceListLinesParams {
  priceListCode: string;
  page?: number;
  size?: number;
}

/** See this file's header comment — a disclosed contract gap, coded against the target shape. */
export const listPriceListLines = async (
  params: ListPriceListLinesParams,
): Promise<PriceLineResponse[]> =>
  (
    await apiClient.get<PriceLineResponse[]>('/api/v1/catalog/pricing/price-lines', {
      params,
    })
  ).data;

/**
 * The canonical retail-store directory (design spec §3/A3) — the SAME listing
 * `features/channel-stock`'s `StoresPanel` consumes. FSD forbids a cross-feature `api/` import,
 * so this is a local copy, mirroring the `features/inventory/api/locationsApi.ts` precedent.
 */
export const listRetailStores = async (): Promise<RetailStoreOption[]> =>
  (await apiClient.get<RetailStoreOption[]>('/api/v1/inventory/availability/retail-stores')).data;
